/* eslint-disable */
/**

 * Simple AST-like parser to analyze Verilog designs and build a logical directed gate graph.

 * Supports:

 * - Module input/output port definitions (both header and body declarations)

 * - Continuous assignments (`assign out = a ^ b;`)

 * - Primitive gate instantiations (`xor u1 (w1, a, b);`)

 */

export const parseVerilog = (code) => {

  if (!code) return { inputs: [], outputs: [], wires: [], gates: [] };



  // Strip single-line and multi-line comments

  const cleanCode = code

    .replace(/\/\/.*$/gm, '')

    .replace(/\/\*[\s\S]*?\*\//g, '');



  const inputs = [];

  const outputs = [];

  const wires = [];

  const gates = [];

  let gateIdCounter = 1;



  // 1. Extract inputs, outputs, and wires from body declarations

  // e.g. input a, b;  input wire [1:0] bus;

  const inputRegex = /\binput\b\s*(?:wire|logic)?\s*(?:\[\d+:\d+\])?\s*([^;]+);/g;

  let match;

  while ((match = inputRegex.exec(cleanCode)) !== null) {

    const ports = match[1].split(',').map(s => s.trim());

    inputs.push(...ports);

  }



  const outputRegex = /\boutput\b\s*(?:wire|logic|reg)?\s*(?:\[\d+:\d+\])?\s*([^;]+);/g;

  while ((match = outputRegex.exec(cleanCode)) !== null) {

    const ports = match[1].split(',').map(s => s.trim());

    outputs.push(...ports);

  }



  const wireRegex = /\bwire\b\s*(?:\[\d+:\d+\])?\s*([^;]+);/g;

  while ((match = wireRegex.exec(cleanCode)) !== null) {

    const ports = match[1].split(',').map(s => s.trim());

    wires.push(...ports);

  }



  // 2. Extract inline ports from module definition header

  // e.g. module my_mod( input a, input logic b, output wire out );

  const inlinePortRegex = /\b(input|output)\b\s*(?:wire|logic|reg)?\s*(?:\[\d+:\d+\])?\s*([a-zA-Z_][a-zA-Z0-9_]*)/g;

  while ((match = inlinePortRegex.exec(cleanCode)) !== null) {

    const direction = match[1];

    const name = match[2];

    if (direction === 'input' && !inputs.includes(name)) {

      inputs.push(name);

    } else if (direction === 'output' && !outputs.includes(name)) {

      outputs.push(name);

    }

  }



  // 3. Parse continuous assignments

  // e.g. assign w1 = a ^ b;

  const assignRegex = /\bassign\b\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*=\s*([^;]+);/g;

  while ((match = assignRegex.exec(cleanCode)) !== null) {

    const dest = match[1].trim();

    const expr = match[2].trim();

    const cleanExpr = expr.replace(/\s+/g, '');



    // Heuristically construct logic gates from boolean expressions

    if (cleanExpr.includes('^')) {

      const parts = expr.split('^').map(p => p.replace(/[()]/g, '').trim());

      gates.push({

        id: gateIdCounter++,

        type: 'Sky130_XOR2',

        name: `xor_${dest}`,

        inputs: parts,

        outputs: [dest],

        expression: expr

      });

    } else if (cleanExpr.includes('&')) {

      const parts = expr.split('&').map(p => p.replace(/[()]/g, '').trim());

      gates.push({

        id: gateIdCounter++,

        type: 'Sky130_AND2',

        name: `and_${dest}`,

        inputs: parts,

        outputs: [dest],

        expression: expr

      });

    } else if (cleanExpr.includes('|')) {

      const parts = expr.split('|').map(p => p.replace(/[()]/g, '').trim());

      gates.push({

        id: gateIdCounter++,

        type: 'Sky130_OR2',

        name: `or_${dest}`,

        inputs: parts,

        outputs: [dest],

        expression: expr

      });

    } else if (cleanExpr.startsWith('~')) {

      const operand = cleanExpr.substring(1).replace(/[()]/g, '').trim();

      gates.push({

        id: gateIdCounter++,

        type: 'Sky130_NOT',

        name: `not_${dest}`,

        inputs: [operand],

        outputs: [dest],

        expression: expr

      });

    } else {

      // Direct assignment (buffer)

      gates.push({

        id: gateIdCounter++,

        type: 'Sky130_BUF',

        name: `buf_${dest}`,

        inputs: [expr],

        outputs: [dest],

        expression: expr

      });

    }

  }



  // 4. Parse standard gate primitives instantiations

  // e.g. xor g1 (w1, a, b);  not inv1 (out, in);

  const gateRegex = /\b(and|or|xor|nand|nor|xnor|not|buf)\b\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*\(([^)]+)\);/g;

  while ((match = gateRegex.exec(cleanCode)) !== null) {

    const type = match[1].toLowerCase();

    const name = match[2];

    const pins = match[3].split(',').map(p => p.trim());

    if (pins.length >= 2) {

      const out = pins[0];

      const ins = pins.slice(1);

      

      let skyType = `Sky130_${type.toUpperCase()}2`;

      if (type === 'not') skyType = 'Sky130_NOT';

      if (type === 'buf') skyType = 'Sky130_BUF';



      gates.push({

        id: gateIdCounter++,

        type: skyType,

        name,

        inputs: ins,

        outputs: [out]

      });

    }

  }



  // Clean duplicate arrays

  return {

    inputs: [...new Set(inputs)].filter(n => n.match(/^[a-zA-Z0-9_]+$/)),

    outputs: [...new Set(outputs)].filter(n => n.match(/^[a-zA-Z0-9_]+$/)),

    wires: [...new Set(wires)].filter(n => n.match(/^[a-zA-Z0-9_]+$/)),

    gates

  };

};



/**

 * Propagates logic values through a parsed directed graph.

 * @param {Object} parsedGraph - Output of parseVerilog

 * @param {Object} inputVals - Current logic values of inputs (e.g. { a: 1, b: 0, cin: 1 })

 * @returns {Object} All net values (e.g. { a: 1, b: 0, cin: 1, w1: 1, sum: 0, cout: 1 })

 */

export const propagateLogic = (parsedGraph, inputVals) => {

  const values = { ...inputVals };

  const { gates, outputs, wires } = parsedGraph;



  // Initialize all outputs and wires to 0 if not present

  outputs.forEach(o => { if (values[o] === undefined) values[o] = 0; });

  wires.forEach(w => { if (values[w] === undefined) values[w] = 0; });



  // Simple topological propagation up to 10 iterations to resolve dependencies

  for (let iter = 0; iter < 10; iter++) {

    let changed = false;

    gates.forEach(gate => {

      const getVal = (name) => values[name] || 0;

      const inputs = gate.inputs.map(getVal);

      let calculatedVal = 0;



      if (gate.type.includes('XOR')) {

        calculatedVal = inputs.reduce((acc, val) => acc ^ val, 0);

      } else if (gate.type.includes('AND')) {

        calculatedVal = inputs.length > 0 ? inputs.reduce((acc, val) => acc & val, 1) : 0;

      } else if (gate.type.includes('OR')) {

        calculatedVal = inputs.reduce((acc, val) => acc | val, 0);

      } else if (gate.type.includes('NOT')) {

        calculatedVal = inputs[0] ? 0 : 1;

      } else if (gate.type.includes('NAND')) {

        calculatedVal = inputs.length > 0 ? (inputs.reduce((acc, val) => acc & val, 1) ? 0 : 1) : 1;

      } else if (gate.type.includes('NOR')) {

        calculatedVal = inputs.reduce((acc, val) => acc | val, 0) ? 0 : 1;

      } else if (gate.type.includes('XNOR')) {

        calculatedVal = inputs.reduce((acc, val) => acc ^ val, 0) ? 0 : 1;

      } else if (gate.type.includes('BUF')) {

        calculatedVal = inputs[0] || 0;

      }



      const outPin = gate.outputs[0];

      if (values[outPin] !== calculatedVal) {

        values[outPin] = calculatedVal;

        changed = true;

      }

    });

    if (!changed) break;

  }



  return values;

};



/**

 * Parses Yosys compiler output JSON into standard schematic gate representation.

 * @param {Object} jsonObj - Yosys output JSON netlist

 * @returns {Object} Parse representation with inputs, outputs, wires, and cell gates.

 */

export const parseYosysJson = (jsonObj) => {

  if (!jsonObj || !jsonObj.modules) return null;

  

  const moduleNames = Object.keys(jsonObj.modules);

  if (moduleNames.length === 0) return null;

  const modName = moduleNames[0];

  const mod = jsonObj.modules[modName];



  const inputs = [];

  const outputs = [];

  const wires = [];

  const gates = [];

  let gateIdCounter = 1;



  const bitToNetName = {};

  

  if (mod.netnames) {

    Object.keys(mod.netnames).forEach(netName => {

      const net = mod.netnames[netName];

      net.bits.forEach(bit => {

        bitToNetName[bit] = netName;

      });

    });

  }



  // Parse ports

  if (mod.ports) {

    Object.keys(mod.ports).forEach(portName => {

      const port = mod.ports[portName];

      const bitName = port.bits.map(b => bitToNetName[b] || `net_${b}`).join('_');

      if (port.direction === 'input') {

        inputs.push(bitName);

      } else if (port.direction === 'output') {

        outputs.push(bitName);

      }

    });

  }



  // Parse cell instances

  if (mod.cells) {

    Object.keys(mod.cells).forEach(cellName => {

      const cell = mod.cells[cellName];

      const type = cell.type;

      

      let skyType = 'Sky130_BUF';

      const uType = (type || '').toUpperCase();

      if (uType.includes('XOR')) skyType = 'Sky130_XOR2';

      else if (uType.includes('NAND')) skyType = 'Sky130_NAND2';

      else if (uType.includes('NOR')) skyType = 'Sky130_NOR2';

      else if (uType.includes('XNOR')) skyType = 'Sky130_XNOR2';

      else if (uType.includes('AND')) skyType = 'Sky130_AND2';

      else if (uType.includes('OR')) skyType = 'Sky130_OR2';

      else if (uType.includes('NOT') || uType.includes('INV')) skyType = 'Sky130_NOT';

      else if (uType.includes('DFF') || uType.includes('ADFF')) skyType = 'Sky130_DFF';

      else if (uType.includes('MUX')) skyType = 'Sky130_MUX';

      else if (uType.includes('ADD')) skyType = 'Sky130_ADD';

      else if (uType.includes('SUB')) skyType = 'Sky130_SUB';

      else if (uType.includes('BUF')) skyType = 'Sky130_BUF';



      const gateInputs = [];

      const gateOutputs = [];

      

      if (cell.connections) {

        Object.keys(cell.connections).forEach(pin => {

          const bits = cell.connections[pin];

          const pinName = bits.map(b => bitToNetName[b] || `net_${b}`).join('_');

          

          const dir = cell.port_directions ? cell.port_directions[pin] : 'input';

          if (dir === 'input' || pin === 'A' || pin === 'B' || pin === 'C') {

            gateInputs.push(pinName);

          } else {

            gateOutputs.push(pinName);

          }

        });

      }



      gates.push({

        id: gateIdCounter++,

        type: skyType,

        name: cellName.replace('$', '').replace('$', '_'),

        inputs: gateInputs,

        outputs: gateOutputs.length > 0 ? gateOutputs : [`net_out_${gateIdCounter}`]

      });

    });

  }



  return {

    inputs: [...new Set(inputs)],

    outputs: [...new Set(outputs)],

    wires,

    gates

  };

};

